import React, { useState } from 'react';
import { ColumnButton } from 'lib/components/ColumnButton';
import api from 'api';
import { ESnapshotExists, ESnapshot } from 'lib/types/firebase';
import { ENotice, EUser } from 'lib/types';
import { EOrganization } from 'lib/types/organization';
import { AddIcon } from 'icons';
import ToastActions from 'redux/toast';
import { useAppDispatch } from 'redux/hooks';
import { Modal } from 'lib/components/Modal';
import { TextField } from 'lib/components/TextField';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Form } from 'lib/components/Form';
import { Alert } from 'lib/components/Alert';

const MAX_ADDRESSEES = 5;

// TODO: Why are we making a separate network request for every email address rather than passing a list to the backend?
const sendReminders = async ({
  emails,
  invoiceId,
  noticeId,
  newspaperName
}: {
  emails: string[];
  invoiceId?: string;
  noticeId?: string;
  newspaperName?: string;
}) => {
  if (!invoiceId) return;

  await Promise.all(
    emails.map(email =>
      api.post('notifications/invoice-reminder', {
        invoiceId,
        noticeId,
        newspaperName,
        email
      })
    )
  );
};

export default function SendReminderModal({
  filer,
  notice,
  invoiceId,
  newspaper,
  onCloseModal
}: {
  filer?: ESnapshotExists<EUser>;
  notice?: ESnapshotExists<ENotice>;
  invoiceId?: string;
  newspaper: ESnapshot<EOrganization>;
  onCloseModal: () => void;
}) {
  const dispatch = useAppDispatch();

  const invoiceRecipient = notice?.data()?.invoiceRecipient;
  const defaultEmail =
    invoiceRecipient?.type === 'email'
      ? invoiceRecipient?.email
      : filer?.data()?.email || '';

  const [emails, setEmails] = useState<string[]>([defaultEmail]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const addEmail = () => {
    setEmails(emails.concat(''));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await sendReminders({
        emails,
        invoiceId,
        noticeId: notice?.id,
        newspaperName: newspaper.data()?.name
      });
      setLoading(false);
      onCloseModal();
      dispatch(
        ToastActions.toastSuccess({
          headerText: 'Success',
          bodyText: `Success! Your invoice reminder${
            emails.length > 1 ? 's have' : ' has'
          } been sent.`
        })
      );
    } catch (error) {
      setLoading(false);
      setErrorMessage(
        'Please try again. If the problem persists, please contact support.'
      );
    }
  };

  return (
    <Modal
      id="send-reminder-modal"
      title="How would you like to send this reminder?"
      onClose={onCloseModal}
      primaryAction={{
        buttonText: 'Send reminder',
        type: 'submit',
        formId: 'send-reminder-emails'
      }}
      loading={loading}
    >
      <Form id={'send-reminder-emails'} onSubmit={handleSubmit}>
        <div className="my-4 space-y-2">
          {errorMessage && (
            <Alert
              id="send-reminder-error"
              status="error"
              title="Error sending reminder"
              description={errorMessage}
            />
          )}
          <p>A copy of the invoice will be attached in an email reminder.</p>
          {emails.map((email, i) => {
            return (
              <div
                key={`${i}-email-container`}
                className="flex gap-2 items-center"
              >
                <div className="flex-1">
                  <TextField
                    id={`email-recipient-${i}`}
                    type="email"
                    required
                    labelText={`Email recipient ${i + 1}`}
                    value={email}
                    checkDependentValidations={value => {
                      if (!value) {
                        return '';
                      }
                      // make sure there are no duplicates
                      const previous = emails.slice(0, i);
                      if (previous.includes(value)) {
                        return 'Please enter a unique email address';
                      }

                      return '';
                    }}
                    onChange={value => {
                      setEmails(
                        emails.map((email2, j) => (i === j ? value : email2))
                      );
                    }}
                    placeholder="Email address"
                  />
                </div>
                {i > 0 && (
                  <ColumnButton
                    type="button"
                    link
                    id={`delete-email-${i}`}
                    onClick={() => {
                      const newEmails = emails
                        .slice(0, i)
                        .concat(emails.slice(i + 1, emails.length));
                      setEmails(newEmails);
                    }}
                    startIcon={<XMarkIcon className="w-5 h-5" />}
                  />
                )}
              </div>
            );
          })}
          <ColumnButton
            type="button"
            secondary
            id="addEmail"
            onClick={addEmail}
            buttonText="Add email"
            disabled={emails.length >= MAX_ADDRESSEES}
            startIcon={<AddIcon className="w-5 h-5 mr-1" />}
          />
        </div>
      </Form>
    </Modal>
  );
}
